import { useEffect, useState } from "react";
import { HERO_PHOTOS } from "@/data/photos";

const ROTATION_MS = 7000;

const useHeroRotation = (length) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (length <= 1) return undefined;
    const id = setInterval(() => {
      setIdx((current) => (current + 1) % length);
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, [length]);
  return [idx, setIdx];
};

function HeroBackground({ idx }) {
  return (
    <div className="absolute inset-0">
      {HERO_PHOTOS.map((p, i) => (
        <img
          key={p.src}
          src={p.src}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out ${
            i === idx ? "opacity-70" : "opacity-0"
          }`}
          loading={i === 0 ? "eager" : "lazy"}
          data-testid={`hero-slide-${i}`}
        />
      ))}
    </div>
  );
}

function HeroQuote({ photo, i, active }) {
  return (
    <blockquote
      className={`absolute transition-opacity duration-[1400ms] max-w-2xl ${
        active ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      data-testid={`hero-quote-${i}`}
    >
      <p className="font-display italic text-2xl sm:text-3xl lg:text-4xl text-paper/95 leading-tight">
        «{photo.quote}»
      </p>
      {photo.author && (
        <cite className="not-italic font-mono text-[0.7rem] tracking-[0.35em] uppercase text-paper/60 mt-3 block">
          — {photo.author}
        </cite>
      )}
    </blockquote>
  );
}

function HeroPagination({ idx, onSelect }) {
  return (
    <div className="flex items-center gap-2">
      {HERO_PHOTOS.map((p, i) => (
        <button
          key={p.src}
          onClick={() => onSelect(i)}
          className={`w-6 sm:w-8 h-px transition-colors ${
            i === idx ? "bg-paper" : "bg-paper/30 hover:bg-paper/60"
          }`}
          aria-label={`Slide ${i + 1}`}
          data-testid={`hero-dot-${i}`}
        />
      ))}
    </div>
  );
}

function Hero() {
  const [idx, setIdx] = useHeroRotation(HERO_PHOTOS.length);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[100vh] w-full bg-ink text-paper overflow-hidden">
      <HeroBackground idx={idx} />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/25 to-ink/85 grain" />

      <div className="relative z-10 min-h-[100vh] flex flex-col justify-between px-6 sm:px-10 py-24 sm:py-28 max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between font-mono text-[0.65rem] sm:text-[0.7rem] tracking-[0.3em] uppercase text-paper/70">
          <span>Est. 1981</span>
          <span className="hidden sm:inline">
            N° {String(idx + 1).padStart(3, "0")} / {String(HERO_PHOTOS.length).padStart(3, "0")}
          </span>
        </div>

        <div className="max-w-5xl">
          <h1
            className="font-display font-black tracking-tight leading-[0.9] text-paper"
            style={{ fontSize: "clamp(4rem, 15vw, 12rem)" }}
            data-testid="hero-title"
          >
            Simon <span className="italic font-normal">O.</span>
          </h1>
          <div className="mt-6 max-w-2xl min-h-[6rem] sm:min-h-[7rem]">
            {HERO_PHOTOS.map((p, i) => (
              <HeroQuote key={p.src} photo={p} i={i} active={i === idx} />
            ))}
          </div>
        </div>

        <div className="flex items-end justify-between font-mono text-[0.6rem] sm:text-[0.65rem] tracking-[0.3em] uppercase text-paper/50">
          <HeroPagination idx={idx} onSelect={setIdx} />
          <span className="hidden sm:inline">Scroll ↓</span>
        </div>
      </div>
    </section>
  );
}

export default Hero;
